import { useEffect, useMemo } from 'react';
import { isEqual } from 'lodash';

import { kebabCase } from '@makemydeal/dr-common-utils';
import { GENERIC_LAST_FIELD_CHANGED_ERROR, MILES_PER_YEAR } from '../../../constants';
import { useSelector, useDispatch } from 'react-redux';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as offerReduxActionCreators from '@makemydeal/dr-dash-store/actions/offerRedux';
import * as deskingSelectors from '@makemydeal/dr-dash-store/selectors/deskingSelectors';
import * as offerActionTypes from '@makemydeal/dr-dash-store/actionTypes/offerActionTypes';
import * as dealScreenErrorActionCreators from '@makemydeal/dr-dash-store/actions/dealScreenErrorActionCreators';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { DealTabs } from '@makemydeal/dr-shared-types';
import { dealLockHooks } from '../../../hooks';
import { useHeldSelector } from '../../../hooks/useHeldSelector';

export const useMilesPerYearField = ({ isSummaryTab }: { isSummaryTab?: boolean }) => {
    const dispatch = useDispatch();
    const isDealScreenRedDotErrorsEnabled = useSelector(featureToggleSelectors.isDealScreenRedDotErrorsEnabled);
    const selected = useHeldSelector(offerReduxSelectors.getAnnualMiles);
    const annualMilesOptions = useSelector(offerReduxSelectors.getAnnualMilesOptions, isEqual);
    const isLocked = dealLockHooks.useIsDealLocked();

    const fieldName = kebabCase(MILES_PER_YEAR);
    const leaseMileageOptions = annualMilesOptions.map(({ id, name }) => ({
        id,
        label: name.toString(),
        value: id.toString()
    }));

    const handleValueChange = (event: any) => {
        const selectedMileage = event.target.value;

        if (selectedMileage) {
            dispatch(offerReduxActionCreators.selectedAnnualMiles(selectedMileage));
        }
    };

    const failed = useSelector(deskingSelectors.wasTheLastFailure(offerActionTypes.SELECTED_ANNUAL_MILES));
    const failedMessage = useMemo(() => (failed ? GENERIC_LAST_FIELD_CHANGED_ERROR : undefined), [failed]);

    useEffect(() => {
        if (isDealScreenRedDotErrorsEnabled && isSummaryTab) {
            dispatch(dealScreenErrorActionCreators.setFieldError(DealTabs.SUMMARY, fieldName, failed));
        }
    }, [isDealScreenRedDotErrorsEnabled, dispatch, failed, isSummaryTab]);

    return {
        label: MILES_PER_YEAR,
        selectProps: {
            name: fieldName,
            onChange: handleValueChange,
            value: selected.toString(),
            options: leaseMileageOptions,
            errorMessage: failedMessage,
            hasError: failedMessage ? true : false,
            displayDeselectOption: false,
            'data-testid': fieldName,
            size: 'sm' as const,
            disabled: isLocked
        }
    };
};
